import React, { useMemo } from 'react';
import Link from 'next/link';
import config from '@cardash/config';
import PropTypes from 'prop-types';
import { Spinner } from 'reactstrap';
import styled from 'styled-components';
import colors from '../../colors';
import useMainContext from '../../../hooks/main-provider';
import { isSfacOrganization } from '../../../utils';
import { useSidebar } from '../../../contexts/SidebarContext';
import { STATIC_ASSETS_URL } from '../../constants';
import UserDropdown from './UserDropdown';
import SidebarOrganizations from './SidebarOrganizations';

const pathsToIgnore = ['/', '/oil-change-pricing', '/appointments-old'];
const HomePageName = 'Home';

const Icons = {
  aiChat: `${STATIC_ASSETS_URL}/icons/ai-chat.svg`,
  aiDviAdvisor: `${STATIC_ASSETS_URL}/icons/ai-dvi-advisor.svg`,
  appointments: `${STATIC_ASSETS_URL}/icons/appointments.svg`,
  home: `${STATIC_ASSETS_URL}/icons/home.svg`,
  leaderboards: `${STATIC_ASSETS_URL}/icons/leaderboards.svg`,
  marketing: `${STATIC_ASSETS_URL}/icons/marketing.svg`,
  reports: `${STATIC_ASSETS_URL}/icons/reports.svg`,
  scheduling: `${STATIC_ASSETS_URL}/icons/scheduling.svg`,
  scorecards: `${STATIC_ASSETS_URL}/icons/scorecards.svg`,
  services: `${STATIC_ASSETS_URL}/icons/services.svg`,
  settings: `${STATIC_ASSETS_URL}/icons/settings.svg`,
  team: `${STATIC_ASSETS_URL}/icons/team.svg`,
  coupon: `${STATIC_ASSETS_URL}/coupon-icon.svg`,
  logo: `${STATIC_ASSETS_URL}/sfac_logo white.png`,
};

const getIconForPage = pageName => {
  const iconMap = {
    Home: Icons.home,
    Appointments: Icons.appointments,
    'Appointments Old': Icons.appointments,
    Leaderboards: Icons.leaderboards,
    Scorecards: Icons.scorecards,
    Reports: Icons.reports,
    Team: Icons.team,
    Coupons: Icons.coupon,
    Scheduling: Icons.scheduling,
    Services: Icons.services,
    Settings: Icons.settings,
    Marketing: Icons.marketing,
    'AI Chat': Icons.aiChat,
    'DVI Advisor': Icons.aiDviAdvisor,
  };
  return iconMap[pageName];
};

const SidebarWrapper = styled.div`
  width: ${props => props.width};
  min-width: ${props => props.width};
  transition: all 0.3s ease;
`;

const SidebarContainer = styled.div`
  width: ${props => props.width};
  min-width: ${props => props.width};
  background-color: ${colors.brand};
  height: 100%;
  min-height: 100vh;
  padding: ${props => (props.loading ? '100px 10px' : '15px 0 0 0')};
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.loading ? 'center' : 'flex-start')};
  justify-content: flex-start;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.3);
`;

const LogoContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${props => (props.collapsed ? 'center' : 'flex-start')};
  align-items: center;
  padding: ${props => (props.collapsed ? '0 10px 10px 20px' : '0 20px 10px')};
  margin-bottom: 10px;
  gap: 12px;
  overflow: visible;
`;

const MenuItemsContainer = styled.div`
  width: 100%;
  margin-top: 10px;
`;

const MenuItem = styled.div`
  padding: ${props => (props.collapsed ? '12px' : '12px 14px')};
  color: #fff;
  background-color: ${props => (props.selected ? '#9575CD' : 'transparent')};
  display: flex;
  align-items: center;
  gap: ${props => (props.collapsed ? '0' : '12px')};
  transition: all 0.2s ease;
  justify-content: ${props => (props.collapsed ? 'center' : 'flex-start')};
  border-radius: 8px;
  cursor: pointer;
  opacity: ${props => (props.selected ? 1 : 0.85)};
  margin: 4px 8px;

  &:hover {
    background-color: ${props => (props.selected ? '#9575CD' : 'rgba(255, 255, 255, 0.1)')};
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
`;

const MenuIcon = styled.img`
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
  opacity: ${props => (props.selected ? 1 : 0.85)};
`;

const MenuText = styled.span`
  font-size: 14px;
  white-space: nowrap;
  font-weight: ${props => (props.selected ? 700 : 300)};
`;

const StyledLink = styled(Link)`
  text-decoration: none !important;

  &:hover {
    text-decoration: none !important;
  }
`;

const MenuSidebar = ({ pages, selectedPageName }) => {
  const { viewMode, selectedShop, selectedOrganization, viewModeDataLoading } = useMainContext();
  const { isCollapsed, sidebarWidth: contextSidebarWidth } = useSidebar();

  const pathsToIgnoreForSidebar = useMemo(
    () => (!isSfacOrganization(selectedOrganization?.id) ? [...pathsToIgnore, '/chat'] : [...pathsToIgnore]),
    [selectedOrganization]
  );

  const sidebarItems = useMemo(
    () => pages.filter(item => !pathsToIgnoreForSidebar.includes(item.path)),
    [pages, pathsToIgnoreForSidebar]
  );

  const isLocationViewMode = useMemo(() => viewMode === config.viewModes.Location, [viewMode]);

  const sidebarWidth = `${contextSidebarWidth}px`;

  if (viewModeDataLoading) {
    return (
      <SidebarWrapper width={sidebarWidth}>
        <SidebarContainer width={sidebarWidth} loading>
          <Spinner color="light" />
        </SidebarContainer>
      </SidebarWrapper>
    );
  }

  return (
    <SidebarWrapper width={sidebarWidth}>
      <SidebarContainer width={sidebarWidth}>
        <LogoContainer collapsed={isCollapsed}>
          <SidebarOrganizations isCollapsed={isCollapsed} />
        </LogoContainer>
        <MenuItemsContainer>
          <StyledLink href={isLocationViewMode ? `/?locationId=${selectedShop?.id}` : '/'}>
            <MenuItem
              collapsed={isCollapsed}
              selected={selectedPageName === HomePageName}
              title={isCollapsed ? HomePageName : undefined}
            >
              <IconWrapper>
                <MenuIcon
                  src={getIconForPage(HomePageName)}
                  alt={HomePageName}
                  selected={selectedPageName === HomePageName}
                />
              </IconWrapper>
              {!isCollapsed && <MenuText selected={selectedPageName === HomePageName}>{HomePageName}</MenuText>}
            </MenuItem>
          </StyledLink>
          {sidebarItems.map(page => (
            <StyledLink
              key={page.path}
              href={isLocationViewMode ? `${page.path}?locationId=${selectedShop?.id}` : `${page.path}`}
            >
              <MenuItem
                collapsed={isCollapsed}
                selected={selectedPageName === page.pageName}
                title={isCollapsed ? page.pageName : undefined}
              >
                <IconWrapper>
                  <MenuIcon
                    src={getIconForPage(page.pageName)}
                    alt={page.pageName}
                    selected={selectedPageName === page.pageName}
                  />
                </IconWrapper>
                {!isCollapsed && <MenuText selected={selectedPageName === page.pageName}>{page.pageName}</MenuText>}
              </MenuItem>
            </StyledLink>
          ))}
        </MenuItemsContainer>
        <UserDropdown />
      </SidebarContainer>
    </SidebarWrapper>
  );
};

MenuSidebar.propTypes = {
  pages: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default MenuSidebar;
